
import { Checkbox } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { Chart } from "chart.js";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChartFont } from "..";
import { DashboardERMModel } from "../../../../models/dashboad/dashboard.model";
import { AMOUNT_UTIL } from "../../../../utils/amount.utils";
import UTIL from "../../../../utils/utils";


interface Props {
    chartId: string,
    title: string;
    data: DashboardERMModel[];
}

const DashboardControlFailure = (props: Props) => {

    const chart = useRef<Chart>();
    const [data, setData] = useState<DashboardERMModel[]>([]);
    const [selected, setSelected] = useState<string[]>([]);
    const { t } = useTranslation();

    const getChartData = (value: DashboardERMModel[]) => {
        const datasets: any[] = [];
        const selectedItems: string[] = [];
        let labels: string[] = [];
        value.forEach((x, index) => {
            datasets.push({
                label: x.label,
                data: Object.values(x.data),
                backgroundColor: UTIL.randomColor(x.label),
                hidden: false,
                barPercentage: 0.5
            });

            selectedItems.push(x.label);
            labels = [...labels, ...Object.keys(x.data)];
        })

        setSelected(selectedItems);
        labels = labels.filter((x, index) => labels.indexOf(x) === index);
        return {
            labels: labels,
            datasets: datasets
        }
    }

    const checkBoxChange = (e: CheckboxChangeEvent, label: string) => {

        if (chart.current) {
            chart.current.data.datasets.forEach(dataset => {
                if (dataset.label === label) {
                    dataset.hidden = !e.target.checked;
                }
            });

            chart.current.update();
        }

        setSelected(p => {
            if (e.target.checked) {
                p.push(label);

            } else {
                p = p.filter(x => x !== label);
            }

            return [...p];
        })

    }

    const createChart = (chartData: any) => {
        const element: any = document.getElementById(props.chartId);
        const ctx = element.getContext('2d');


        chart.current = new Chart(ctx, {
            type: 'bar',
            data: chartData,
            options: {
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            // Include a dollar sign in the ticks
                            callback: function (value, index, values) {
                                return AMOUNT_UTIL.convertNumericToFormattedAmount(Number(value), 0);
                            },
                            font: ChartFont,
                            color: "#969696"
                        }
                    },
                    x: {
                        ticks: {
                            font: ChartFont,
                            color: "#969696"
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: false
                    }
                }
            },
        });
    }

    const updateChart = (chartData: any) => {
        if (chart.current) {
            chart.current.data = chartData;
            chart.current.update();
        }
    }

    useEffect(() => {
        data.forEach(item => {
            const checkBox = document.getElementById(item.checkboxId);
            if (checkBox && checkBox.nextElementSibling) {
                const element = checkBox.nextElementSibling as HTMLElement;
                element.style.backgroundColor = item.chartColor;
                element.style.borderColor = item.chartColor;
            }
        })
    }, [data])

    useEffect(() => {
        if (props.data && props.data.length) {
            const chartData = getChartData(props.data);
            if (chart.current) {
                updateChart(chartData);
            } else {
                createChart(chartData);
            }
            setData(props.data);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.data])

    return (
        <div className="chart-wrapper">
            <p className="title">
                {props.title}
            </p>
            <p className="chart-tilte">
                {!selected || selected.length === 0 ? <span>&nbsp;</span> : selected.length === 1 ? selected[0] : t("Dashboard.lbMultipleControls")}
            </p>
            <canvas height={200} id={props.chartId}></canvas>
            <div className="select-wrapper">
                {
                    data.map((item) => (
                        <Checkbox
                            key={item.label}
                            checked={selected.includes(item.label)}
                            onChange={(e) => checkBoxChange(e, item.label)}
                            id={item.checkboxId}>
                            {item.label}
                        </Checkbox>
                    ))
                }
            </div>
        </div>
    )
}

export default React.memo(DashboardControlFailure)